import { toast } from "sonner";
import { AlertTriangle, Loader2, RotateCcw, Trash2 } from "lucide-react";
import { clipAudioUrl, deleteClip, retryClip } from "@/lib/myVoicesApi";
import { safeApiError } from "@/lib/safeErrorText";

const PHASE = { queued: "Waiting", isolating: "Removing the music", cleaning: "Cleaning and removing silences" };
const run = async (fn, ok, onChanged) => {
  try { await fn(); if (ok) toast.success(ok); onChanged(); } catch (e) { toast.error("Didn't work", { description: safeApiError(e) }); }
};

function ClipRow({ voiceId, clip, onChanged }) {
  const m = clip.meta || {};
  return (
    <li data-testid={`clip-${clip.id}`} className="py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-medium">{clip.name}</div>
          <div className="truncate text-[11px] text-white/45">{clip.source?.label}{clip.isolate ? " · music removed" : ""}</div>
        </div>
        {clip.status === "ready" ? <span data-testid={`clip-minutes-${clip.id}`} className="font-mono text-[12px] tabular-nums text-cyan-200">{(m.singing_sec / 60).toFixed(1)} min</span> : null}
        {clip.status === "processing" ? <span className="flex items-center gap-1.5 text-[12px] text-amber-200"><Loader2 className="h-3.5 w-3.5 animate-spin" />{PHASE[clip.phase] || "Working"}…</span> : null}
        {clip.status === "error" ? (
          <button type="button" data-testid={`clip-retry-${clip.id}`} onClick={() => run(() => retryClip(voiceId, clip.id), null, onChanged)} className="flex items-center gap-1 text-[12px] text-rose-200 hover:text-white">
            <RotateCcw className="h-3.5 w-3.5" /> Try again
          </button>
        ) : null}
        <button type="button" data-testid={`clip-delete-${clip.id}`} aria-label="Remove" disabled={clip.status === "processing"}
          onClick={() => run(() => deleteClip(voiceId, clip.id), "Removed", onChanged)}
          className="grid h-8 w-8 place-items-center rounded-full text-white/40 transition-colors hover:bg-rose-500/15 hover:text-rose-200 disabled:opacity-30">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      {clip.error ? <p className="mt-1 text-[12px] text-rose-200">{clip.error}</p> : null}
      {(m.warnings || []).length ? (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {m.warnings.map((w) => <span key={w} className="flex items-center gap-1 rounded-full bg-amber-300/10 px-2 py-0.5 text-[11px] text-amber-100"><AlertTriangle className="h-3 w-3" />{w}</span>)}
        </div>
      ) : null}
      {clip.status === "ready" ? <audio data-testid={`clip-audio-${clip.id}`} controls preload="none" src={clipAudioUrl(voiceId, clip.id)} className="mt-2 h-8 w-full" /> : null}
    </li>
  );
}

/** Training recordings, newest last, with clean-up status and a listen-back of what the model learns from. */
export default function ClipList({ voiceId, clips, onChanged }) {
  if (!clips?.length) return <p data-testid="clips-empty" className="py-4 text-[13px] text-white/50">No recordings yet — add your first a cappella vocal above.</p>;
  return <ul data-testid="clip-list" className="divide-y divide-white/[0.06]">{clips.map((c) => <ClipRow key={c.id} voiceId={voiceId} clip={c} onChanged={onChanged} />)}</ul>;
}
